import { AddIcon, Button, Select, Text, type TypeSelectOption, dialogService } from '@thefarmersfront/kitchen';

import type {
  ColDef,
  GetRowIdParams,
  GridOptions,
  ICellRendererParams,
  RowHeightParams,
  ValueFormatterParams,
} from 'ag-grid-community';

import { ClaimExchangeDeliveryItemCell } from '~/components/claim';
import { CheckboxCell, MultiRowCell, NumberInputCell, TableHeaderWithTooltipIcon } from '~/components/common';

import { formatNumber, getSelectPlacement } from '~/utils/common';

import { DEFAULT_INPUT_STATUS } from '~/constants';

import type {
  TypeExchangeRegisterSecondStepValues,
  TypeExchangeRegisterSecondStepValuesState,
} from '~/types/claim.types';

type TypeExchangeOrderItem = TypeExchangeRegisterSecondStepValues['exchangeOrderItems'][number];

/**
 * [교환 신청/조회] 교환 신청 등록 모달 > 교환 정보 입력 > 교환 상품 테이블 컬럼 정의
 */
const getClaimExchangeRegisterExchangeItemsColumnDefs = ({
  data: tableData,
  dataState,
  selectOptions,
  onChange,
  onChangeState,
  onOpenExchangeItemAddModal,
}: {
  data: TypeExchangeOrderItem[];
  dataState: TypeExchangeRegisterSecondStepValuesState['exchangeOrderItems'];
  selectOptions: TypeSelectOption<string>[];
  onChange: (value: Partial<TypeExchangeRegisterSecondStepValues>) => void;
  onChangeState: (value: Partial<TypeExchangeRegisterSecondStepValuesState>) => void;
  onOpenExchangeItemAddModal: (itemId: TypeExchangeOrderItem['itemId']) => void;
}) => {
  const gridOptions: GridOptions = {
    suppressScrollOnNewData: true,
    getRowId: (params: GetRowIdParams<TypeExchangeOrderItem>) =>
      `${params.data.itemId}-${params.data.exchangeDeliveryItems.length}`,
    getRowHeight: ({ data }: RowHeightParams<TypeExchangeOrderItem>) =>
      data?.exchangeDeliveryItems?.length ? data.exchangeDeliveryItems.length * 40 : 40,
    columnTypes: { multiRow: { cellStyle: { padding: 0, border: 0 } } },
  };

  const columnDefs: ColDef<TypeExchangeOrderItem>[] = [
    {
      field: 'itemId',
      headerName: '상품코드',
      width: 130,
    },
    {
      field: 'itemName',
      headerName: '상품명',
      minWidth: 340,
      flex: 1,
    },
    {
      field: 'isExchange',
      headerName: '교환여부',
      width: 76,
      minWidth: 76,
      cellRenderer: CheckboxCell,
      cellRendererParams: ({
        data,
      }: ICellRendererParams<TypeExchangeOrderItem, TypeExchangeOrderItem['isExchange']>) => ({
        label: '교환',
        onChange: (e: React.ChangeEvent<HTMLInputElement>) => {
          onChange({
            exchangeOrderItems: tableData.map((item) =>
              item.itemId === data?.itemId
                ? {
                    ...item,
                    isExchange: e.target.checked,
                    exchangeQuantity: '',
                    exchangeReason: null,
                    isSameGoods: false,
                    exchangeDeliveryItems: [],
                  }
                : item
            ),
          });
          onChangeState({
            exchangeOrderItems: dataState.map((item) =>
              item.itemId === data?.itemId
                ? {
                    ...item,
                    exchangeQuantity: DEFAULT_INPUT_STATUS,
                    exchangeReason: DEFAULT_INPUT_STATUS,
                    exchangeDeliveryItems: [],
                  }
                : item
            ),
          });
        },
      }),
    },
    {
      field: 'quantity',
      headerName: '구매수량',
      width: 80,
      cellStyle: { textAlign: 'right' },
      valueFormatter: ({ value }: ValueFormatterParams<TypeExchangeOrderItem, TypeExchangeOrderItem['quantity']>) =>
        value ? formatNumber(value) : '-',
    },
    {
      field: 'exchangeQuantity',
      headerName: '교환수량',
      headerComponent: TableHeaderWithTooltipIcon,
      headerComponentParams: { tooltipDescription: '필수 입력입니다.' },
      width: 100,
      minWidth: 100,
      cellRenderer: NumberInputCell,
      cellRendererParams: ({
        data,
      }: ICellRendererParams<TypeExchangeOrderItem, TypeExchangeOrderItem['exchangeQuantity']>) => ({
        disabled: !data?.isExchange,
        status: dataState.find((item) => item.itemId === data?.itemId)?.exchangeQuantity.status,
        validationMessage: dataState.find((item) => item.itemId === data?.itemId)?.exchangeQuantity.message,
        onChange: (value: string) => {
          onChange({
            exchangeOrderItems: tableData.map((item) =>
              item.itemId === data?.itemId ? { ...item, exchangeQuantity: value } : item
            ),
          });
          onChangeState({
            exchangeOrderItems: dataState.map((item) =>
              item.itemId === data?.itemId ? { ...item, exchangeQuantity: DEFAULT_INPUT_STATUS } : item
            ),
          });
        },
        onBlur: (value: string) => {
          const numberValue = Number(value.trim());
          const quantity = tableData.find((item) => item.itemId === data?.itemId)?.quantity ?? 0;

          if (numberValue === 0) {
            onChange({
              exchangeOrderItems: tableData.map((item) =>
                item.itemId === data?.itemId ? { ...item, exchangeQuantity: '' } : item
              ),
            });
            return;
          }

          if (numberValue > quantity) {
            onChangeState({
              exchangeOrderItems: dataState.map((item) =>
                item.itemId === data?.itemId ? { ...item, exchangeQuantity: { status: 'error' } } : item
              ),
            });

            dialogService.addDialog({
              children: '교환수량은 구매수량보다 같거나 적은 수량만 입력할 수 있습니다.',
              onConfirm: () => {
                onChange({
                  exchangeOrderItems: tableData.map((item) =>
                    item.itemId === data?.itemId ? { ...item, exchangeQuantity: '' } : item
                  ),
                });
                onChangeState({
                  exchangeOrderItems: dataState.map((item) =>
                    item.itemId === data?.itemId ? { ...item, exchangeQuantity: DEFAULT_INPUT_STATUS } : item
                  ),
                });
              },
            });
          }
        },
        onFocus: () =>
          onChangeState({
            exchangeOrderItems: dataState.map((item) =>
              item.itemId === data?.itemId ? { ...item, exchangeQuantity: DEFAULT_INPUT_STATUS } : item
            ),
          }),
      }),
    },
    {
      headerName: '교환사유',
      headerComponent: TableHeaderWithTooltipIcon,
      headerComponentParams: { tooltipDescription: '필수 선택입니다.' },
      width: 147,
      minWidth: 147,
      cellRenderer: Select,
      cellRendererParams: ({
        node,
        data,
      }: ICellRendererParams<TypeExchangeOrderItem, TypeExchangeOrderItem['exchangeReason']>) => ({
        size: 'tiny',
        placeholder: '선택',
        dropdownPortalTarget: document.body,
        shouldCloseOnNotInView: true,
        dropdownPlacement: getSelectPlacement(node.rowIndex, tableData.length, { thresholdRow: 5 }),
        disabled: !data?.isExchange,
        options: selectOptions,
        status: dataState.find((item) => item.itemId === data?.itemId)?.exchangeReason.status,
        validationMessage: dataState.find((item) => item.itemId === data?.itemId)?.exchangeReason.message,
        value: data?.exchangeReason ?? null,
        onChange: (_: unknown, value: TypeSelectOption<string>) => {
          onChange({
            exchangeOrderItems: tableData.map((item) =>
              item.itemId === data?.itemId ? { ...item, exchangeReason: value } : item
            ),
          });
          onChangeState({
            exchangeOrderItems: dataState.map((item) =>
              item.itemId === data?.itemId ? { ...item, exchangeReason: DEFAULT_INPUT_STATUS } : item
            ),
          });
        },
      }),
    },
    {
      field: 'isSameGoods',
      headerName: '동일상품 교환여부',
      headerComponent: TableHeaderWithTooltipIcon,
      headerComponentParams: {
        tooltipDescription:
          '동일한 상품을 재배송한다면 체크해주세요.\n만약 다른 상품을 재배송한다면 [추가] 버튼을 눌러\n배송할 상품을 선택해주세요.',
        tooltipMaxWidth: '258px',
      },
      width: 148,
      minWidth: 76,
      cellRenderer: CheckboxCell,
      cellRendererParams: ({
        data,
      }: ICellRendererParams<TypeExchangeOrderItem, TypeExchangeOrderItem['isSameGoods']>) => ({
        label: '동일',
        disabled: !data?.isExchange,
        onChange: (e: React.ChangeEvent<HTMLInputElement>) => {
          const isChecked = e.target.checked;

          onChange({
            exchangeOrderItems: tableData.map((item) =>
              item.itemId === data?.itemId
                ? {
                    ...item,
                    isSameGoods: isChecked,
                    exchangeDeliveryItems: isChecked
                      ? [{ itemName: item.itemName, itemId: item.itemId, quantity: '' }]
                      : [],
                  }
                : item
            ),
          });
          onChangeState({
            exchangeOrderItems: dataState.map((item) =>
              item.itemId === data?.itemId ? { ...item, exchangeDeliveryItems: [] } : item
            ),
          });
        },
      }),
    },
    {
      headerName: '',
      width: 80,
      minWidth: 80,
      cellRenderer: Button,
      cellRendererParams: ({ data }: ICellRendererParams<TypeExchangeOrderItem>) => ({
        size: 'tiny',
        color: 'tertiary',
        leftIcon: <AddIcon size={16} />,
        children: '추가',
        disabled: !data?.isExchange,
        onClick: () => {
          if (!data?.itemId) return;
          onOpenExchangeItemAddModal(data.itemId);
        },
      }),
    },
    {
      field: 'exchangeDeliveryItems',
      headerName: '교환배송 상품코드',
      width: 130,
      cellDataType: false,
      cellRenderer: MultiRowCell,
      cellRendererParams: {
        field: 'itemId',
        itemRenderer: Text,
        itemRendererParams: ({
          value,
        }: ICellRendererParams<
          TypeExchangeOrderItem,
          TypeExchangeOrderItem['exchangeDeliveryItems'][number]['itemId']
        >) => ({
          size: 'large',
          children: value ?? '-',
        }),
      },
      type: 'multiRow',
    },
    {
      field: 'exchangeDeliveryItems',
      headerName: '교환배송 상품명',
      width: 340,
      cellDataType: false,
      cellRenderer: MultiRowCell,
      cellRendererParams: {
        field: 'itemName',
        itemRenderer: ClaimExchangeDeliveryItemCell,
        itemRendererParams: ({
          data: exchangeItem,
          node,
        }: ICellRendererParams<
          TypeExchangeOrderItem,
          TypeExchangeOrderItem['exchangeDeliveryItems'][number]['itemName']
        >) => {
          const parentRowData = node?.data;

          return {
            onClickDeleteButton: () => {
              onChange({
                exchangeOrderItems: tableData.map((item) => {
                  const newExchangeDeliveryItems = item.exchangeDeliveryItems.filter(
                    (item) => item.itemId !== exchangeItem?.itemId
                  );
                  const isSameGoods =
                    newExchangeDeliveryItems.length === 1 &&
                    newExchangeDeliveryItems.some((newItem) => newItem.itemId === parentRowData?.itemId);

                  return item.itemId === parentRowData?.itemId
                    ? {
                        ...item,
                        isSameGoods,
                        exchangeDeliveryItems: newExchangeDeliveryItems,
                      }
                    : item;
                }),
              });
              onChangeState({
                exchangeOrderItems: dataState.map((item) =>
                  item.itemId === parentRowData?.itemId
                    ? {
                        ...item,
                        exchangeDeliveryItems: item.exchangeDeliveryItems.filter(
                          (item) => item.itemId !== exchangeItem?.itemId
                        ),
                      }
                    : item
                ),
              });
            },
          };
        },
      },
      type: 'multiRow',
    },
    {
      field: 'exchangeDeliveryItems',
      headerName: '교환배송수량',
      headerComponent: TableHeaderWithTooltipIcon,
      headerComponentParams: { tooltipDescription: '필수 입력입니다.' },
      width: 117,
      minWidth: 117,
      cellDataType: false,
      cellRenderer: MultiRowCell,
      cellRendererParams: {
        field: 'quantity',
        showDashIfNoValue: false,
        itemRenderer: NumberInputCell,
        itemRendererParams: ({
          data: exchangeItem,
          node,
        }: ICellRendererParams<
          TypeExchangeOrderItem,
          TypeExchangeOrderItem['exchangeDeliveryItems'][number]['quantity']
        >) => {
          const parentRowData = node?.data;

          return {
            disabled: (parentRowData?.exchangeDeliveryItems.length ?? 0) === 0,
            status: dataState
              .find((item) => item.itemId === parentRowData?.itemId)
              ?.exchangeDeliveryItems.find((item) => item.itemId === exchangeItem?.itemId)?.quantity.status,
            validationMessage: dataState
              .find((item) => item.itemId === parentRowData?.itemId)
              ?.exchangeDeliveryItems.find((item) => item.itemId === exchangeItem?.itemId)?.quantity.message,
            onChange: (value: string) => {
              onChange({
                exchangeOrderItems: tableData.map((item) =>
                  item.itemId === parentRowData?.itemId
                    ? {
                        ...item,
                        exchangeDeliveryItems: item.exchangeDeliveryItems.map((item) =>
                          item.itemId === exchangeItem?.itemId ? { ...item, quantity: value } : item
                        ),
                      }
                    : item
                ),
              });
              onChangeState({
                exchangeOrderItems: dataState.map((item) =>
                  item.itemId === parentRowData?.itemId
                    ? {
                        ...item,
                        exchangeDeliveryItems: item.exchangeDeliveryItems.map((item) =>
                          item.itemId === exchangeItem?.itemId ? { ...item, quantity: DEFAULT_INPUT_STATUS } : item
                        ),
                      }
                    : item
                ),
              });
            },
            onFocus: () =>
              onChangeState({
                exchangeOrderItems: dataState.map((item) =>
                  item.itemId === parentRowData?.itemId
                    ? {
                        ...item,
                        exchangeDeliveryItems: item.exchangeDeliveryItems.map((item) =>
                          item.itemId === exchangeItem?.itemId ? { ...item, quantity: DEFAULT_INPUT_STATUS } : item
                        ),
                      }
                    : item
                ),
              }),
            onBlur: (value: string) => {
              const numberValue = Number(value.trim());

              if (numberValue === 0) {
                onChange({
                  exchangeOrderItems: tableData.map((item) =>
                    item.itemId === parentRowData?.itemId
                      ? {
                          ...item,
                          exchangeDeliveryItems: item.exchangeDeliveryItems.map((item) =>
                            item.itemId === exchangeItem?.itemId ? { ...item, quantity: '' } : item
                          ),
                        }
                      : item
                  ),
                });
              }
            },
          };
        },
      },
      type: 'multiRow',
    },
  ];

  return { columnDefs, gridOptions };
};

export default getClaimExchangeRegisterExchangeItemsColumnDefs;
